import {
  Button, Chip, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow, Tooltip,
} from '@heroui/react';
import { AlertTriangle, ChevronLeft, ChevronRight, FileText, RefreshCw } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ContentLoadError, PageTitle, PageWrapper, Spinner } from '../../../components';
import { CurrencyTotals, useGetPlatformFees, useGetPlatformStripeSummary } from '../../../queries';
import {
  HotelLink,
  Muted,
  Pagination,
  SubText,
  TableCard,
  TileHint,
  TileLabel,
  TileMeta,
  TilesRow,
  TileValue,
  Toolbar,
  WarnText,
  WhiteCard,
} from './StripeDashboard.styled';

/**
 * Dashboard płatności HotelSpot — ile zarabiamy i skąd.
 *
 * Dane pochodzą ze Stripe'a przez nasz backend (`verifyAdminAccess`), a nie z rejestru
 * w Firestore. Rejestr powstaje z webhooków, więc jedna nieudana dostawa zostawiłaby
 * w nim dziurę — przy pieniądzach źródłem musi być stan faktyczny u operatora.
 *
 * SALDO i PROWIZJE ZA OKRES to celowo dwie osobne liczby. Saldo mówi, ile mamy u Stripe'a
 * teraz; prowizje — ile naliczyliśmy w wybranym okresie. Nie muszą się zgadzać, bo wypłaty
 * i zwroty przesuwają saldo niezależnie od tego, kiedy prowizja powstała.
 */

const RANGES = [
  { label: '7 dni', days: 7 },
  { label: '30 dni', days: 30 },
  { label: '90 dni', days: 90 },
  { label: 'Wszystko', days: 0 },
];

const OUTLET_KIND_LABELS: Record<string, string> = {
  roomService: 'Room service',
  productsServices: 'Sklep / usługi',
  productRental: 'Wypożyczalnia',
  spa: 'Spa',
  breakfastService: 'Śniadania',
};

/** Kwoty ze Stripe'a są w najmniejszej jednostce waluty — dzielimy dopiero przy wyświetlaniu. */
const money = (minor: number, currency: string): string => new Intl.NumberFormat('pl-PL', {
  style: 'currency',
  currency: currency || 'PLN',
}).format((minor || 0) / 100);

const dateTime = (epochSeconds: number): string => new Date(epochSeconds * 1000)
  .toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' });

export const StripeDashboard = () => {
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  // Stos kursorów — Stripe paginuje kursorowo, więc „wstecz" wymaga zapamiętania drogi.
  const [cursors, setCursors] = useState<Array<string | null>>([null]);
  const [pageIndex, setPageIndex] = useState(0);

  const range = useMemo(() => (days
    ? { createdGte: Math.floor(Date.now() / 1000) - days * 24 * 60 * 60 }
    : {}), [days]);

  const summary = useGetPlatformStripeSummary(range);
  const fees = useGetPlatformFees(range, cursors[pageIndex] ?? null);

  const changeRange = (nextDays: number): void => {
    setDays(nextDays);
    setCursors([null]);
    setPageIndex(0);
  };

  const goNext = (): void => {
    const next = fees.data?.nextCursor;
    if (!next) return;
    setCursors((prev) => [...prev.slice(0, pageIndex + 1), next]);
    setPageIndex((prev) => prev + 1);
  };

  const totals: CurrencyTotals[] = summary.data?.totals ?? [];
  // Jeden loader na cały ekran — dwa osobne spinnery robiły z niego migającą szachownicę.
  const isLoading = summary.isPending || fees.isPending;

  if (summary.isError || fees.isError) {
    return <PageWrapper><ContentLoadError /></PageWrapper>;
  }

  return (
    <PageWrapper>
      <PageTitle title="Stripe — prowizje platformy" />

      <Toolbar>
        {RANGES.map((option) => (
          <Button
            key={option.label}
            size="sm"
            variant={days === option.days ? 'solid' : 'bordered'}
            color={days === option.days ? 'primary' : 'default'}
            isDisabled={isLoading}
            onPress={() => changeRange(option.days)}
          >
            {option.label}
          </Button>
        ))}
        <Button
          size="sm"
          variant="light"
          startContent={<RefreshCw size={14} />}
          isDisabled={isLoading}
          onPress={() => { summary.refetch(); fees.refetch(); }}
        >
          Odśwież
        </Button>
        {/* Publikacja nowej wersji regulaminu płatności — osobny ekran, bo to jedyne
            miejsce, w którym zmieniamy dokument obowiązujący wszystkie hotele. */}
        <Button
          size="sm"
          variant="bordered"
          color="primary"
          startContent={<FileText size={14} />}
          onPress={() => navigate('/stripe/terms')}
        >
          Regulamin płatności
        </Button>
        {summary.data && !summary.data.livemode && (
          <Chip size="sm" color="warning" variant="flat">tryb testowy</Chip>
        )}
      </Toolbar>

      {isLoading ? <Spinner /> : (
        <>
          <TilesRow>
            <WhiteCard>
              <TileLabel>Saldo dostępne</TileLabel>
              {summary.data?.balance.available.length
                ? summary.data.balance.available.map((b) => (
                  <TileValue key={b.currency}>{money(b.amount, b.currency)}</TileValue>
                ))
                : <TileValue><Muted>—</Muted></TileValue>}
              <TileHint>Środki gotowe do wypłaty na nasze konto.</TileHint>
            </WhiteCard>

            <WhiteCard>
              <TileLabel>Saldo w drodze</TileLabel>
              {summary.data?.balance.pending.length
                ? summary.data.balance.pending.map((b) => (
                  <TileValue key={b.currency}>{money(b.amount, b.currency)}</TileValue>
                ))
                : <TileValue><Muted>—</Muted></TileValue>}
              <TileHint>Płatności jeszcze nierozliczone przez Stripe.</TileHint>
            </WhiteCard>

            <WhiteCard>
              <TileLabel>Prowizje w okresie</TileLabel>
              {totals.length ? totals.map((total) => (
                <div key={total.currency}>
                  <TileValue>{money(total.feeNet, total.currency)}</TileValue>
                  <TileMeta>
                    {`pobrane ${money(total.fee, total.currency)} · oddane ${money(total.feeRefunded, total.currency)} · ${total.count} transakcji`}
                  </TileMeta>
                </div>
              )) : <TileValue><Muted>—</Muted></TileValue>}
              <TileHint>
                {summary.data?.truncated
                  ? 'Liczby niepełne — okres ma więcej transakcji, niż zdążyliśmy zsumować.'
                  : 'Pobrane minus oddane przy zwrotach.'}
              </TileHint>
            </WhiteCard>

            <WhiteCard>
              <TileLabel>Obrót hoteli</TileLabel>
              {totals.length ? totals.map((total) => (
                <TileValue key={total.currency}>{money(total.gross, total.currency)}</TileValue>
              )) : <TileValue><Muted>—</Muted></TileValue>}
              <TileHint>Zapłacone przez gości — trafia na konta hoteli, nie do nas.</TileHint>
            </WhiteCard>
          </TilesRow>

          {summary.data?.truncated && (
            <Toolbar>
              <AlertTriangle size={16} color="#b45309" />
              <TileMeta>Podsumowanie ucięte — zawęź okres, żeby zobaczyć pełne kwoty.</TileMeta>
            </Toolbar>
          )}

          <TableCard>
            <Table aria-label="Prowizje platformy" removeWrapper isStriped>
              <TableHeader>
                <TableColumn>Data</TableColumn>
                <TableColumn>Hotel</TableColumn>
                <TableColumn>Punkt sprzedaży</TableColumn>
                <TableColumn>Zamówienie</TableColumn>
                <TableColumn align="end">Kwota gościa</TableColumn>
                <TableColumn align="end">Nasza prowizja</TableColumn>
              </TableHeader>
              <TableBody emptyContent="Brak prowizji w tym okresie">
                {(fees.data?.rows ?? []).map((row) => (
                  <TableRow key={row.feeId}>
                    <TableCell className="whitespace-nowrap">{dateTime(row.created)}</TableCell>

                    <TableCell>
                      {row.hotelId ? (
                        <HotelLink type="button" onClick={() => navigate(`/organizations/${row.hotelId}`)}>
                          {row.hotelName || row.hotelId}
                        </HotelLink>
                      ) : <Muted>—</Muted>}
                      <Tooltip content={row.accountId} size="sm" radius="sm" delay={300}>
                        <SubText>{row.accountId ? `${row.accountId.slice(0, 11)}…` : '—'}</SubText>
                      </Tooltip>
                    </TableCell>

                    <TableCell>
                      {row.outletName || (row.kind ? OUTLET_KIND_LABELS[row.kind] || row.kind : '—')}
                      {row.outletName && row.kind && (
                        <SubText>{OUTLET_KIND_LABELS[row.kind] || row.kind}</SubText>
                      )}
                    </TableCell>

                    <TableCell>
                      <Tooltip content={row.outletId || 'brak outletId w metadanych'} size="sm" radius="sm" delay={300}>
                        <SubText>{row.orderId || '—'}</SubText>
                      </Tooltip>
                    </TableCell>

                    <TableCell className="whitespace-nowrap text-right">
                      {money(row.grossAmount, row.currency)}
                      {row.grossRefunded > 0 && (
                        <WarnText>{`zwrot ${money(row.grossRefunded, row.currency)}`}</WarnText>
                      )}
                    </TableCell>

                    <TableCell className="whitespace-nowrap text-right font-medium">
                      {money(row.feeAmount - row.feeRefunded, row.currency)}
                      {row.feeRefunded > 0 && (
                        <SubText>
                          {`pobrane ${money(row.feeAmount, row.currency)}, oddane ${money(row.feeRefunded, row.currency)}`}
                        </SubText>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableCard>

          <Pagination>
            <Button
              size="sm"
              variant="bordered"
              startContent={<ChevronLeft size={14} />}
              isDisabled={pageIndex === 0}
              onPress={() => setPageIndex((prev) => Math.max(0, prev - 1))}
            >
              Poprzednia
            </Button>
            <Button
              size="sm"
              variant="bordered"
              endContent={<ChevronRight size={14} />}
              isDisabled={!fees.data?.hasMore}
              onPress={goNext}
            >
              Następna
            </Button>
            <TileMeta>{`strona ${pageIndex + 1}`}</TileMeta>
          </Pagination>
        </>
      )}
    </PageWrapper>
  );
};
